import { hasRealEstatePortalEntitlement } from "./realEstatePortalAccess";

function safeStr(x) {
  return (x ?? "").toString();
}

/**
 * Construction-side portal: profile services, explicit assignments, or loaded PM projects.
 */
export function hasConstructionPortalEntitlement(profile, pmProjectCount = 0) {
  if (pmProjectCount > 0) return true;
  if (!profile || typeof profile !== "object") return false;
  const services = profile.services;
  if (Array.isArray(services) && services.includes("construction")) return true;
  if (safeStr(profile.clientType).toLowerCase() === "both") return true;
  const assigned = profile.assignedProjectIds;
  if (Array.isArray(assigned) && assigned.length > 0) return true;
  return false;
}

/**
 * Where to send the user on /client-portal after auth + data load.
 * - real_estate: only real-estate workspace
 * - construction: projects-first construction home
 * - choose: show workspace picker (both entitlements)
 */
export function resolveClientPortalLanding(profile, { pmLoading, pmProjectCount }) {
  if (!profile || pmLoading) return "loading";
  const re = hasRealEstatePortalEntitlement(profile);
  const construction = hasConstructionPortalEntitlement(profile, pmProjectCount);
  if (re && construction) return "choose";
  if (re && !construction) return "real_estate";
  return "construction";
}

export const CLIENT_PORTAL_WORKSPACE_KEY = "clientPortalWorkspaceV1";

/** After user picks a workspace once, do not show the chooser again this session. */
export const CLIENT_PORTAL_PICKER_DONE_KEY = "clientPortalOnboardingV1";

export function readClientPortalWorkspaceChoice() {
  try {
    return safeStr(sessionStorage.getItem(CLIENT_PORTAL_WORKSPACE_KEY)).toLowerCase().trim();
  } catch {
    return "";
  }
}

export function writeClientPortalWorkspaceChoice(value) {
  try {
    const v = safeStr(value).toLowerCase().trim();
    if (v === "construction" || v === "real_estate") {
      sessionStorage.setItem(CLIENT_PORTAL_WORKSPACE_KEY, v);
    }
  } catch {
    // ignore
  }
}

export function hasCompletedPortalWorkspacePicker() {
  try {
    return sessionStorage.getItem(CLIENT_PORTAL_PICKER_DONE_KEY) === "done";
  } catch {
    return false;
  }
}

export function markPortalWorkspacePickerDone() {
  try {
    sessionStorage.setItem(CLIENT_PORTAL_PICKER_DONE_KEY, "done");
  } catch {
    // ignore
  }
}

/**
 * CL-393A-FIX1 / CL-LIVE-001 — Block construction dashboard until landing is resolved.
 * Never hold the loading gate when a recoverable error or setup-pending state exists.
 * Real-estate redirect uses a separate short "Opening…" surface, not this endless gate.
 */
export function shouldShowClientPortalIndexLoadingGate({
  user,
  profile,
  isGuest,
  pmLoading,
  portalLanding,
  isClientPortalIndex,
  error = "",
  setupPending = false,
}) {
  if (!user || isGuest || !isClientPortalIndex) return false;
  if (error || setupPending) return false;
  if (!profile) return true;
  if (pmLoading) return true;
  // RE-only users leave this gate so Navigate / "Opening your workspace…" can run.
  if (portalLanding === "real_estate") return false;
  if (portalLanding === "loading") return true;
  return false;
}

export function shouldShowClientPortalWorkspaceChooser({
  user,
  profile,
  isGuest,
  pmLoading,
  portalLanding,
  workspaceParam,
  isClientPortalIndex,
}) {
  return (
    Boolean(user && profile && !isGuest && !pmLoading && isClientPortalIndex) &&
    portalLanding === "choose" &&
    safeStr(workspaceParam).toLowerCase() !== "construction"
  );
}
